import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Box, Chip, CircularProgress, IconButton, TextField, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { useAssistantStore } from '@/stores/assistantStore';
import { useAssistantChat } from '@/hooks/useAssistantChat';
import {
  ASSISTANT_FILE_ACCEPT,
  ASSISTANT_FILE_MAX_COUNT,
  useAssistantFiles,
  type AssistantFilesApi,
  type AssistantPendingFile,
} from '@/hooks/useAssistantFiles';

interface ChatInputProps {
  /** Shared with the panel so a drop and the composer see the same files. */
  files?: AssistantFilesApi;
}

function fileSizeLabel(
  bytes: number,
  language: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const safe = Number.isFinite(bytes) ? Math.max(0, bytes) : 0;
  const format = new Intl.NumberFormat(language, { maximumFractionDigits: 1 });
  if (safe < 1024) return t('files.sizeBytes', { count: format.format(safe) });
  if (safe < 1024 * 1024) return t('files.sizeKb', { size: format.format(safe / 1024) });
  return t('files.sizeMb', { size: format.format(safe / (1024 * 1024)) });
}

/** Excel and Word put text and an image on the clipboard together. Text wins. */
function pastedPlainText(data: DataTransfer | null): string {
  if (!data || typeof data.getData !== 'function') return '';
  try {
    const text = data.getData('text/plain');
    return typeof text === 'string' ? text : '';
  } catch {
    return '';
  }
}

function PendingFileChip({
  file,
  onRemove,
}: {
  file: AssistantPendingFile;
  onRemove: (localId: string) => void;
}) {
  const { t, i18n } = useTranslation('assistant');
  const pending = file.status === 'uploading' || file.status === 'scanning';
  const chip = (
    <Chip
      size="small"
      color={file.status === 'error' ? 'error' : 'default'}
      variant={file.status === 'error' ? 'filled' : 'outlined'}
      {...(pending
        ? {
            icon: (
              <CircularProgress
                size={14}
                aria-label={t(file.status === 'scanning' ? 'files.scanning' : 'files.uploading')}
              />
            ),
          }
        : {})}
      label={t('files.chipLabel', {
        name: file.fileName,
        size: fileSizeLabel(file.sizeBytes, i18n.language, t),
      })}
      onDelete={() => onRemove(file.localId)}
      deleteIcon={
        <CancelIcon
          aria-label={t('files.remove', { name: file.fileName })}
          aria-hidden={false}
          role="img"
        />
      }
      sx={{ maxWidth: '100%' }}
    />
  );
  if (file.status !== 'error' || !file.errorCode) return chip;
  return <Tooltip title={t(`files.errors.${file.errorCode}`)}>{chip}</Tooltip>;
}

export function ChatInput({ files: controlledFiles }: ChatInputProps) {
  const { t } = useTranslation('assistant');
  const [input, setInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isLoading = useAssistantStore((s) => s.isLoading);
  const { sendMessage } = useAssistantChat();
  const ownFiles = useAssistantFiles();
  const files = controlledFiles ?? ownFiles;
  const hasError = files.files.some((file) => file.status === 'error');
  const canSend =
    !isLoading &&
    !files.busy &&
    !hasError &&
    (input.trim().length > 0 || files.readyIds.length > 0);

  const handleSend = () => {
    const trimmed = input.trim();
    const attachments = files.readyAttachments;
    if (isLoading || files.busy || hasError) return;
    if (!trimmed && attachments.length === 0) return;
    const snapshot = files.files.filter((file) => file.status === 'ready');
    setInput('');
    files.clear();
    const sent = sendMessage(
      trimmed,
      attachments.length > 0 ? attachments : undefined,
      (ok) => {
        if (!ok) files.restore(snapshot);
      },
    );
    // A rejected promise is a failed send too. The hook itself settles with false.
    void Promise.resolve(sent).catch(() => {
      files.restore(snapshot);
    });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLElement>) => {
    if (pastedPlainText(event.clipboardData).length > 0) return;
    const clipboardFiles = event.clipboardData?.files;
    if (!clipboardFiles || clipboardFiles.length === 0) return;
    const images = Array.from(clipboardFiles).filter((file) => file.type.startsWith('image/'));
    if (images.length === 0) return;
    event.preventDefault();
    files.add(images);
  };

  const atCapacity = files.files.length >= ASSISTANT_FILE_MAX_COUNT;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {files.files.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {files.files.map((file) => (
            <PendingFileChip key={file.localId} file={file} onRemove={files.remove} />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          accept={ASSISTANT_FILE_ACCEPT}
          onChange={(event) => {
            const selected = event.target.files;
            if (selected && selected.length > 0) files.add(Array.from(selected));
            event.target.value = '';
          }}
        />
        <Tooltip title={t('files.attach')}>
          <span>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || atCapacity}
              aria-label={t('files.attach')}
            >
              <AttachFileIcon />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={t('placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={isLoading}
          slotProps={{
            input: {
              sx: { borderRadius: 2 },
            },
          }}
        />
        <Tooltip title={t('send')}>
          <span>
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!canSend}
              aria-label={t('send')}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
